import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox, Zap, Users, Send, Plus, Trash2, RefreshCw, MessageSquare,
  Mail, Phone, Circle, X, Power, ShieldCheck, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  available: 'bg-green-500',
  busy: 'bg-red-500',
  away: 'bg-amber-500',
  offline: 'bg-slate-500'
};

export const MedexConnectHub = () => {
  const [tab, setTab] = useState('inbox');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/connect/cpaas/stats`);
      if (res.ok) setStats(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats, tab]);

  return (
    <div className="space-y-6" data-testid="medex-connect-hub">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-cyan-400" /> Medex Connect Hub
          </h2>
          <p className="text-slate-400 text-sm mt-1">Unified inbox, automation rules & team presence</p>
        </div>
        {stats && (
          <div className="flex gap-3 text-xs">
            <div className="glass-card px-4 py-2" data-testid="stats-inbox">
              <span className="text-slate-400">Threads</span>
              <p className="text-white font-bold text-lg">{stats.inbox?.threads ?? 0}</p>
            </div>
            <div className="glass-card px-4 py-2" data-testid="stats-unread">
              <span className="text-slate-400">Unread</span>
              <p className="text-cyan-400 font-bold text-lg">{stats.inbox?.unread ?? 0}</p>
            </div>
            <div className="glass-card px-4 py-2" data-testid="stats-rules">
              <span className="text-slate-400">Active Rules</span>
              <p className="text-amber-400 font-bold text-lg">{stats.rules?.active ?? 0}</p>
            </div>
            <div className="glass-card px-4 py-2" data-testid="stats-agents">
              <span className="text-slate-400">Available</span>
              <p className="text-green-400 font-bold text-lg">{stats.presence?.available ?? 0}</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex space-x-1 bg-white/5 rounded-lg p-1 w-fit">
        {[
          { id: 'inbox', label: 'Unified Inbox', icon: Inbox },
          { id: 'rules', label: 'Rules Engine', icon: Zap },
          { id: 'presence', label: 'Presence', icon: Users },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`connect-tab-${t.id}`}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${tab === t.id ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'inbox' && <UnifiedInbox onChanged={fetchStats} />}
      {tab === 'rules' && <RulesEngine onChanged={fetchStats} />}
      {tab === 'presence' && <PresencePanel onChanged={fetchStats} />}
    </div>
  );
};

// ============ Unified Inbox ============

const UnifiedInbox = ({ onChanged }) => {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newMsg, setNewMsg] = useState({ contact: '', channel: 'sms', message: '', name: '' });
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    const res = await fetch(`${API}/api/connect/inbox/threads`);
    if (res.ok) { const d = await res.json(); setThreads(d.threads || []); }
  }, []);

  const loadMessages = useCallback(async (threadId) => {
    const res = await fetch(`${API}/api/connect/inbox/threads/${threadId}/messages`);
    if (res.ok) {
      const d = await res.json();
      setMessages(d.messages || []);
      fetch(`${API}/api/connect/inbox/threads/${threadId}/read`, { method: 'POST' });
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (selected) loadMessages(selected.thread_id);
  }, [selected, loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true); setError('');
    try {
      const res = await fetch(`${API}/api/connect/inbox/threads/${selected.thread_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: reply })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Send failed'); }
      setReply('');
      await loadMessages(selected.thread_id);
      loadThreads(); onChanged?.();
    } catch (e) { setError(e.message); }
    setSending(false);
  };

  const startNew = async () => {
    setSending(true); setError('');
    try {
      const res = await fetch(`${API}/api/connect/inbox/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newMsg)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Send failed'); }
      setShowNew(false);
      setNewMsg({ contact: '', channel: 'sms', message: '', name: '' });
      loadThreads(); onChanged?.();
    } catch (e) { setError(e.message); }
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="unified-inbox">
      {/* Thread list */}
      <div className="glass-card p-4 lg:col-span-1 max-h-[600px] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">Conversations</h3>
          <div className="flex gap-2">
            <button onClick={loadThreads} className="p-1.5 text-slate-400 hover:text-white" data-testid="inbox-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => setShowNew(true)} className="p-1.5 bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30" data-testid="inbox-new-message-btn">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        {threads.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">No conversations yet. Outbound notifications and inbound SMS will appear here.</p>
        ) : threads.map(t => (
          <button
            key={t.thread_id}
            onClick={() => setSelected(t)}
            data-testid={`thread-${t.thread_id}`}
            className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${selected?.thread_id === t.thread_id ? 'bg-cyan-500/10 border border-cyan-500/30' : 'hover:bg-white/5'}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-white text-sm font-medium flex items-center gap-2">
                {t.last_channel === 'email' ? <Mail className="w-3.5 h-3.5 text-purple-400" /> : <Phone className="w-3.5 h-3.5 text-green-400" />}
                {t.contact_name || t.contact}
              </span>
              {t.unread_count > 0 && (
                <span className="px-1.5 py-0.5 bg-cyan-500 text-black text-xs rounded-full font-bold">{t.unread_count}</span>
              )}
            </div>
            <p className="text-slate-400 text-xs mt-1 truncate">{t.last_message}</p>
            <p className="text-slate-600 text-[10px] mt-0.5">{t.last_message_at?.slice(0, 16).replace('T', ' ')}</p>
          </button>
        ))}
      </div>

      {/* Message view */}
      <div className="glass-card p-4 lg:col-span-2 flex flex-col max-h-[600px]">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <Inbox className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <p>Select a conversation</p>
            </div>
          </div>
        ) : (
          <>
            <div className="border-b border-white/10 pb-3 mb-3">
              <h3 className="text-white font-semibold">{selected.contact_name || selected.contact}</h3>
              <p className="text-slate-500 text-xs">{selected.contact} · {(selected.channels || []).join(', ')}</p>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 mb-3" data-testid="message-list">
              {messages.map(m => (
                <div key={m.message_id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] p-3 rounded-xl text-sm ${m.direction === 'out' ? 'bg-cyan-500/20 text-cyan-50' : 'bg-white/10 text-white'}`}>
                    {m.subject && <p className="font-semibold text-xs mb-1">{m.subject}</p>}
                    <p className="whitespace-pre-wrap">{m.body}</p>
                    <p className="text-[10px] opacity-50 mt-1">
                      {m.channel} · {m.created_at?.slice(11, 16)} {m.status === 'failed' && '· ⚠ failed'}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {error && <p className="text-red-400 text-xs mb-2" data-testid="inbox-error">{error}</p>}
            <div className="flex gap-2">
              <input
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendReply()}
                placeholder={`Reply via ${selected.last_channel || 'sms'}...`}
                data-testid="reply-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
              />
              <button
                onClick={sendReply}
                disabled={sending || !reply.trim()}
                data-testid="reply-send-btn"
                className="px-4 py-2 bg-cyan-500 text-black rounded-lg font-semibold text-sm disabled:opacity-40 hover:bg-cyan-400 flex items-center gap-1"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* New message modal */}
      <AnimatePresence>
        {showNew && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowNew(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 border border-white/10" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold">New Message</h3>
                <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {['sms', 'email'].map(ch => (
                    <button key={ch} onClick={() => setNewMsg({ ...newMsg, channel: ch })}
                      className={`px-3 py-1.5 rounded-lg text-sm capitalize ${newMsg.channel === ch ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-slate-400'}`}>
                      {ch}
                    </button>
                  ))}
                </div>
                <input placeholder={newMsg.channel === 'sms' ? 'Phone number' : 'Email address'} value={newMsg.contact}
                  onChange={e => setNewMsg({ ...newMsg, contact: e.target.value })}
                  data-testid="new-msg-contact-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                <input placeholder="Name (optional)" value={newMsg.name}
                  onChange={e => setNewMsg({ ...newMsg, name: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                <textarea placeholder="Message" rows={3} value={newMsg.message}
                  onChange={e => setNewMsg({ ...newMsg, message: e.target.value })}
                  data-testid="new-msg-body-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                {error && <p className="text-red-400 text-xs">{error}</p>}
                <button onClick={startNew} disabled={sending || !newMsg.contact || !newMsg.message}
                  data-testid="new-msg-send-btn"
                  className="w-full py-2 bg-cyan-500 text-black rounded-lg font-semibold disabled:opacity-40">
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ Rules Engine ============

const EMPTY_RULE = {
  name: '', description: '', trigger_type: 'inbound_sms', event_type: '',
  conditions: [], actions: [], enabled: true
};

const ACTION_TYPES = [
  { value: 'auto_reply', label: 'Auto-Reply SMS', params: ['message'] },
  { value: 'send_sms', label: 'Send SMS', params: ['to', 'message'] },
  { value: 'send_email', label: 'Send Email', params: ['to', 'subject', 'message'] },
  { value: 'forward_sms', label: 'Forward SMS', params: ['to'] },
  { value: 'webhook', label: 'Call Webhook', params: ['url'] },
  { value: 'tag_thread', label: 'Tag Thread', params: ['tag'] },
];

const RulesEngine = ({ onChanged }) => {
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/connect/rules`);
    if (res.ok) { const d = await res.json(); setRules(d.rules || []); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRule = async () => {
    setError('');
    const isNew = !editing.rule_id;
    try {
      const res = await fetch(`${API}/api/connect/rules${isNew ? '' : '/' + editing.rule_id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(JSON.stringify(d.detail || d)); }
      setEditing(null); load(); onChanged?.();
    } catch (e) { setError(e.message); }
  };

  const toggle = async (id) => {
    await fetch(`${API}/api/connect/rules/${id}/toggle`, { method: 'POST' });
    load(); onChanged?.();
  };

  const remove = async (id) => {
    await fetch(`${API}/api/connect/rules/${id}`, { method: 'DELETE' });
    load(); onChanged?.();
  };

  return (
    <div className="space-y-4" data-testid="rules-engine">
      <div className="flex justify-between items-center">
        <p className="text-slate-400 text-sm">No-code automation: when a trigger fires and conditions match, actions run automatically.</p>
        <button onClick={() => setEditing({ ...EMPTY_RULE })} data-testid="rule-create-btn"
          className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-semibold hover:bg-amber-500/30 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="glass-card p-8 text-center text-slate-500">
          <Zap className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No rules yet. Example: "When inbound SMS contains HELP → auto-reply with support info".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rules.map(r => (
            <div key={r.rule_id} className="glass-card p-4" data-testid={`rule-card-${r.rule_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <Circle className={`w-2.5 h-2.5 ${r.enabled ? 'fill-green-500 text-green-500' : 'fill-slate-600 text-slate-600'}`} />
                    {r.name}
                  </h4>
                  <p className="text-slate-500 text-xs mt-0.5">{r.description}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => toggle(r.rule_id)} title={r.enabled ? 'Disable' : 'Enable'}
                    data-testid={`rule-toggle-${r.rule_id}`}
                    className={`p-1.5 rounded ${r.enabled ? 'text-green-400' : 'text-slate-500'} hover:bg-white/10`}>
                    <Power className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditing({ ...r })} className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded">
                    <Zap className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(r.rule_id)} data-testid={`rule-delete-${r.rule_id}`}
                    className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 text-xs space-y-1">
                <p className="text-slate-400">
                  <span className="text-cyan-400 font-mono">WHEN</span> {r.trigger_type === 'inbound_sms' ? 'Inbound SMS' : `Event: ${r.event_type || 'any'}`}
                </p>
                {(r.conditions || []).map((c, i) => (
                  <p key={i} className="text-slate-400"><span className="text-purple-400 font-mono">IF</span> {c.field} {c.op} "{c.value}"</p>
                ))}
                {(r.actions || []).map((a, i) => (
                  <p key={i} className="text-slate-400"><span className="text-amber-400 font-mono">THEN</span> {ACTION_TYPES.find(t => t.value === a.type)?.label || a.type}</p>
                ))}
                <p className="text-slate-600 pt-1">Fired {r.run_count || 0}× {r.last_run_at ? `· last ${r.last_run_at.slice(0, 16).replace('T', ' ')}` : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rule editor modal */}
      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8" onClick={() => setEditing(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-slate-800 rounded-2xl p-6 w-full max-w-lg mx-4 border border-white/10 my-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold">{editing.rule_id ? 'Edit Rule' : 'New Rule'}</h3>
                <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                <input placeholder="Rule name" value={editing.name}
                  onChange={e => setEditing({ ...editing, name: e.target.value })}
                  data-testid="rule-name-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                <input placeholder="Description (optional)" value={editing.description || ''}
                  onChange={e => setEditing({ ...editing, description: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />

                <div>
                  <label className="text-slate-400 text-xs">Trigger</label>
                  <select value={editing.trigger_type} onChange={e => setEditing({ ...editing, trigger_type: e.target.value })}
                    data-testid="rule-trigger-select"
                    className="w-full bg-slate-700 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1">
                    <option value="inbound_sms">Inbound SMS received</option>
                    <option value="event">Platform event fired</option>
                  </select>
                </div>
                {editing.trigger_type === 'event' && (
                  <input placeholder="Event type e.g. credential.approved (blank = any)" value={editing.event_type || ''}
                    onChange={e => setEditing({ ...editing, event_type: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                )}

                <div>
                  <div className="flex justify-between items-center">
                    <label className="text-slate-400 text-xs">Conditions (all must match)</label>
                    <button onClick={() => setEditing({ ...editing, conditions: [...editing.conditions, { field: 'body', op: 'contains', value: '' }] })}
                      className="text-cyan-400 text-xs hover:underline">+ Add</button>
                  </div>
                  {editing.conditions.map((c, i) => (
                    <div key={i} className="flex gap-1 mt-1">
                      <select value={c.field} onChange={e => { const cs = [...editing.conditions]; cs[i].field = e.target.value; setEditing({ ...editing, conditions: cs }); }}
                        className="bg-slate-700 border border-white/10 rounded px-2 py-1.5 text-white text-xs">
                        <option value="body">body</option><option value="from">from</option><option value="to">to</option><option value="event_type">event_type</option><option value="contact">contact</option>
                      </select>
                      <select value={c.op} onChange={e => { const cs = [...editing.conditions]; cs[i].op = e.target.value; setEditing({ ...editing, conditions: cs }); }}
                        className="bg-slate-700 border border-white/10 rounded px-2 py-1.5 text-white text-xs">
                        <option value="contains">contains</option><option value="equals">equals</option><option value="starts_with">starts with</option><option value="not_contains">not contains</option>
                      </select>
                      <input value={c.value} placeholder="value" onChange={e => { const cs = [...editing.conditions]; cs[i].value = e.target.value; setEditing({ ...editing, conditions: cs }); }}
                        className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                      <button onClick={() => setEditing({ ...editing, conditions: editing.conditions.filter((_, j) => j !== i) })}
                        className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <label className="text-slate-400 text-xs">Actions</label>
                    <button onClick={() => setEditing({ ...editing, actions: [...editing.actions, { type: 'auto_reply', params: {} }] })}
                      data-testid="rule-add-action-btn"
                      className="text-amber-400 text-xs hover:underline">+ Add</button>
                  </div>
                  {editing.actions.map((a, i) => {
                    const spec = ACTION_TYPES.find(t => t.value === a.type);
                    return (
                      <div key={i} className="bg-white/5 rounded-lg p-2 mt-1 space-y-1">
                        <div className="flex gap-1 items-center">
                          <select value={a.type} onChange={e => { const as = [...editing.actions]; as[i] = { type: e.target.value, params: {} }; setEditing({ ...editing, actions: as }); }}
                            className="flex-1 bg-slate-700 border border-white/10 rounded px-2 py-1.5 text-white text-xs">
                            {ACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <button onClick={() => setEditing({ ...editing, actions: editing.actions.filter((_, j) => j !== i) })}
                            className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                        </div>
                        {(spec?.params || []).map(p => (
                          <input key={p} placeholder={p + (p === 'message' ? ' (supports {from} {body})' : '')} value={a.params?.[p] || ''}
                            onChange={e => { const as = [...editing.actions]; as[i].params = { ...as[i].params, [p]: e.target.value }; setEditing({ ...editing, actions: as }); }}
                            className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                        ))}
                      </div>
                    );
                  })}
                </div>

                {error && <p className="text-red-400 text-xs">{error}</p>}
                <button onClick={saveRule} disabled={!editing.name || editing.actions.length === 0}
                  data-testid="rule-save-btn"
                  className="w-full py-2 bg-amber-500 text-black rounded-lg font-semibold disabled:opacity-40">
                  Save Rule
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ Presence Panel ============

const PresencePanel = ({ onChanged }) => {
  const [agents, setAgents] = useState([]);
  const [newAgent, setNewAgent] = useState({ name: '', email: '' });
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/connect/presence`);
    if (res.ok) { const d = await res.json(); setAgents(d.agents || []); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (agentId, status) => {
    await fetch(`${API}/api/connect/presence/${agentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    load(); onChanged?.();
  };

  const addAgent = async () => {
    const agentId = 'agent_' + newAgent.email.split('@')[0].replace(/[^a-z0-9]/gi, '_').toLowerCase();
    await fetch(`${API}/api/connect/presence/${agentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'available', name: newAgent.name, email: newAgent.email })
    });
    setShowAdd(false); setNewAgent({ name: '', email: '' });
    load(); onChanged?.();
  };

  const removeAgent = async (agentId) => {
    await fetch(`${API}/api/connect/presence/${agentId}`, { method: 'DELETE' });
    load(); onChanged?.();
  };

  return (
    <div className="space-y-4" data-testid="presence-panel">
      <div className="flex justify-between items-center">
        <p className="text-slate-400 text-sm">Team availability for call routing & escalation.</p>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="presence-add-btn"
          className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm font-semibold hover:bg-green-500/30 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Agent
        </button>
      </div>

      {showAdd && (
        <div className="glass-card p-4 flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[150px]">
            <label className="text-slate-400 text-xs">Name</label>
            <input value={newAgent.name} onChange={e => setNewAgent({ ...newAgent, name: e.target.value })}
              data-testid="agent-name-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1" />
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="text-slate-400 text-xs">Email</label>
            <input value={newAgent.email} onChange={e => setNewAgent({ ...newAgent, email: e.target.value })}
              data-testid="agent-email-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1" />
          </div>
          <button onClick={addAgent} disabled={!newAgent.name || !newAgent.email}
            data-testid="agent-save-btn"
            className="px-4 py-2 bg-green-500 text-black rounded-lg font-semibold text-sm disabled:opacity-40">Add</button>
        </div>
      )}

      {agents.length === 0 ? (
        <div className="glass-card p-8 text-center text-slate-500">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No agents yet. Add your team to track availability.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.map(a => (
            <div key={a.agent_id} className="glass-card p-4" data-testid={`agent-card-${a.agent_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-500/30 flex items-center justify-center text-white font-bold">
                      {(a.name || a.agent_id).charAt(0).toUpperCase()}
                    </div>
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-slate-800 ${STATUS_COLORS[a.status] || 'bg-slate-500'}`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{a.name || a.agent_id}</p>
                    <p className="text-slate-500 text-xs">{a.email}</p>
                  </div>
                </div>
                <button onClick={() => removeAgent(a.agent_id)} className="text-slate-600 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-1 mt-3">
                {Object.keys(STATUS_COLORS).map(s => (
                  <button key={s} onClick={() => setStatus(a.agent_id, s)}
                    data-testid={`agent-status-${a.agent_id}-${s}`}
                    className={`flex-1 py-1 rounded text-[10px] capitalize transition-colors ${a.status === s ? 'bg-white/15 text-white font-semibold' : 'bg-white/5 text-slate-500 hover:text-white'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
